import SvgIcon from '@home/components/SvgIcon';
import React from 'react';
// import ProjectItem from '@gp/components/ProjectItem';
// import PlatformImg from '@gp/components/PlatformIcon/PlatformImg';
import ProjectColumnItem from '@gp/components/ProjectColumnItem';
import { ChangeCheckMemoStatus } from '@gp/enums/changeLog';
import { SortType } from '@gp/enums/common';
import type {
  IPipelineChangeChecklistFilterVO,
  IPipelineChangeChecklistRecordVO,
} from '@gp/types/changeLog';
import type { IRecord } from '@gp/types/global';
import { ProjectColumnBuilders } from '@gp/utils/columns';
import { formatDateStr } from '@gp/utils/common';
import { getDatesByRange } from '@gp/utils/date';
import { toProjectDetails } from '@gp/utils/project';
import type { TableColumn } from 'tea-component';
import type { TableHeaderGroup } from 'tea-component/es/table/addons/groupable';
import {
  TAB_NAME_KEY_LIST,
  TAB_NAME_KEY_MAPPING,
  TAB_NAME_MAPPING,
} from '../../../../../constants';
import PipelineChangeChecklistUpdateStatusTag from '../components/PipelineChangeChecklistUpdateStatusTag';

const CURRENT_UPDATE_HEADER_KEY = 'update__current';
const CURRENT_FINANCE_HEADER_KEY = 'finance__current';

const toProjectDetailsOfChangeChecklist = (
  payload: IRecord,
  ...args: [React.SyntheticEvent | null, boolean]
): string => {
  const { project_id: projectId, project: projectName } = payload || {};
  const params = { id: projectId || '', name: projectName || '' };
  return toProjectDetails(params, ...args);
};

const getUpdateStatusHeaderKey = (key?: string, date?: string): string => {
  if (!key) return '';
  if (!TAB_NAME_KEY_LIST.includes(key)) return '';
  return `${key}__${date || ''}`;
};

const renderDateUpdateStatus = (
  cvm: IPipelineChangeChecklistRecordVO,
  domain: string,
  date?: string,
) => {
  const { update_status_list: updateStatusList } = cvm || {};
  const updateStatusItem = updateStatusList?.find((item) => item?.date === date);
  const isChecked = updateStatusItem?.update_status?.[domain] === ChangeCheckMemoStatus.Checked;
  if (!isChecked) return null;
  return (
    <SvgIcon className="dbg-pipeline-change-log-change-checklist-check-icon" type="right-good" />
  );
};

const getDateUpdateStatusColumns = (
  date: string,
  headerKeys: {
    detailUpdateStatusHeaderKey: string;
    milestoneUpdateStatusHeaderKey: string;
    updateUpdateStatusHeaderKey: string;
    financeUpdateStatusHeaderKey: string;
    stakeholderUpdateStatusHeaderKey: string;
    techUpdateStatusHeaderKey: string;
  },
): TableColumn<IPipelineChangeChecklistRecordVO>[] => {
  const {
    detailUpdateStatusHeaderKey,
    milestoneUpdateStatusHeaderKey,
    updateUpdateStatusHeaderKey,
    financeUpdateStatusHeaderKey,
    stakeholderUpdateStatusHeaderKey,
    techUpdateStatusHeaderKey,
  } = headerKeys || {};

  return [
    {
      key: detailUpdateStatusHeaderKey,
      align: 'center',
      header: TAB_NAME_MAPPING[TAB_NAME_KEY_MAPPING.OVERVIEW],
      width: 120,
      render: (cvm) => {
        return renderDateUpdateStatus(cvm, TAB_NAME_KEY_MAPPING.OVERVIEW, date);
      },
    },
    {
      key: milestoneUpdateStatusHeaderKey,
      align: 'center',
      header: TAB_NAME_MAPPING[TAB_NAME_KEY_MAPPING.MILESTONE],
      width: 120,
      render: (cvm) => {
        return renderDateUpdateStatus(cvm, TAB_NAME_KEY_MAPPING.MILESTONE, date);
      },
    },
    {
      key: updateUpdateStatusHeaderKey,
      align: 'center',
      header: TAB_NAME_MAPPING[TAB_NAME_KEY_MAPPING.UPDATE],
      width: 100,
      render: (cvm) => {
        return renderDateUpdateStatus(cvm, TAB_NAME_KEY_MAPPING.UPDATE, date);
      },
    },
    {
      key: financeUpdateStatusHeaderKey,
      align: 'center',
      header: TAB_NAME_MAPPING[TAB_NAME_KEY_MAPPING.FINANCE],
      width: 100,
      render: (cvm) => {
        return renderDateUpdateStatus(cvm, TAB_NAME_KEY_MAPPING.FINANCE, date);
      },
    },
    {
      key: stakeholderUpdateStatusHeaderKey,
      align: 'center',
      header: TAB_NAME_MAPPING[TAB_NAME_KEY_MAPPING.STAKEHOLDER],
      width: 130,
      render: (cvm) => {
        return renderDateUpdateStatus(cvm, TAB_NAME_KEY_MAPPING.STAKEHOLDER, date);
      },
    },
    {
      key: techUpdateStatusHeaderKey,
      align: 'center',
      header: TAB_NAME_MAPPING[TAB_NAME_KEY_MAPPING.TECHNICAL],
      width: 100,
      render: (cvm) => {
        return renderDateUpdateStatus(cvm, TAB_NAME_KEY_MAPPING.TECHNICAL, date);
      },
    },
  ];
};

export const analysisDynamicMetadata = (
  filter?: IPipelineChangeChecklistFilterVO,
): {
  headerGroups: TableHeaderGroup[];
  columns: TableColumn<IPipelineChangeChecklistRecordVO>[];
} => {
  const { rangePicker } = filter || {};
  const { dateType, startDate, endDate } = rangePicker || {};
  const endDateFormatted = dateType !== 'month' ? endDate : formatDateStr(endDate, 'YYYY-MM');
  const dates = getDatesByRange(dateType, startDate, endDate, SortType.Desc);
  const headerGroups: TableHeaderGroup[] = [
    {
      key: `${CURRENT_UPDATE_HEADER_KEY}__${endDate}`,
      title: <div className="dbg-pipeline-change-log-title-top">Update</div>,
      align: 'left',
      children: [CURRENT_UPDATE_HEADER_KEY],
    },
    {
      key: `${CURRENT_FINANCE_HEADER_KEY}__${endDate}`,
      title: <div className="dbg-pipeline-change-log-title-top">Finance</div>,
      align: 'left',
      children: [CURRENT_FINANCE_HEADER_KEY],
    },
  ];
  const columns: TableColumn<IPipelineChangeChecklistRecordVO>[] = [
    {
      key: CURRENT_UPDATE_HEADER_KEY,
      align: 'left',
      header: <div className="dbg-pipeline-change-log-title-bottom">{endDateFormatted}</div> ,
      width: 170,
      fixed: 'left',
      render: (cvm) => {
        const { update_memo: updateMemo } = cvm || {};
        return <PipelineChangeChecklistUpdateStatusTag status={updateMemo} />;
      },
    },
    {
      key: CURRENT_FINANCE_HEADER_KEY,
      align: 'left',
      header: <div className="dbg-pipeline-change-log-title-bottom">{endDateFormatted}</div>,
      fixed: 'left',
      width: 170,
      render: (cvm) => {
        const { finance_memo: financeMemo } = cvm || {};
        return <PipelineChangeChecklistUpdateStatusTag status={financeMemo} />;
      },
    },
  ];

  for (const date of dates) {
    const dateFormatted = dateType !== 'month' ? date : formatDateStr(date, 'YYYY-MM');
    const detailUpdateStatusHeaderKey = getUpdateStatusHeaderKey(
      TAB_NAME_KEY_MAPPING.OVERVIEW,
      date,
    );
    const milestoneUpdateStatusHeaderKey = getUpdateStatusHeaderKey(
      TAB_NAME_KEY_MAPPING.MILESTONE,
      date,
    );
    const updateUpdateStatusHeaderKey = getUpdateStatusHeaderKey(TAB_NAME_KEY_MAPPING.UPDATE, date);
    const financeUpdateStatusHeaderKey = getUpdateStatusHeaderKey(
      TAB_NAME_KEY_MAPPING.FINANCE,
      date,
    );
    const stakeholderUpdateStatusHeaderKey = getUpdateStatusHeaderKey(
      TAB_NAME_KEY_MAPPING.STAKEHOLDER,
      date,
    );
    const techUpdateStatusHeaderKey = getUpdateStatusHeaderKey(
      TAB_NAME_KEY_MAPPING.TECHNICAL,
      date,
    );
    const headerGroup: TableHeaderGroup = {
      key: `update_status__${date}`,
      title: `${dateFormatted} Update Status`,
      align: 'center',
      children: [
        detailUpdateStatusHeaderKey,
        milestoneUpdateStatusHeaderKey,
        updateUpdateStatusHeaderKey,
        financeUpdateStatusHeaderKey,
        stakeholderUpdateStatusHeaderKey,
        techUpdateStatusHeaderKey,
      ],
    };
    const dateColumns = getDateUpdateStatusColumns(date, {
      detailUpdateStatusHeaderKey,
      milestoneUpdateStatusHeaderKey,
      updateUpdateStatusHeaderKey,
      financeUpdateStatusHeaderKey,
      stakeholderUpdateStatusHeaderKey,
      techUpdateStatusHeaderKey,
    });
    Array.prototype.push.apply(columns, dateColumns);
    headerGroups.push(headerGroup);
  }

  return { headerGroups, columns };
};

export const getColumns = (props?: {}): TableColumn<IPipelineChangeChecklistRecordVO>[] => {
  const {} = props || {};

  return [
    {
      key: 'project_id',
      align: 'left',
      header: 'Project',
      width: 304,
      fixed: 'left',
      render: (cvm: any) => <ProjectColumnItem data={cvm} isShowStage={true} />,
    },
    ProjectColumnBuilders.buildProjectContactsColumn({
      key: 'key_contact',
      align: 'left',
      header: (
        <div>
          <div>P-team</div>
          <div>Contact</div>
        </div>
      ),
    }),
    ProjectColumnBuilders.buildProjectContactsColumn({
      key: 'finance',
      align: 'left',
      header: (
        <div>
          <div>Finance</div>
          <div>Contact</div>
        </div>
      ),
    }),
  ];
};
